import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Select, Input } from 'govuk-react-jsx';
import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../../features/app/appSlice';
import {
  getManufacturerModels,
  setManufacturers,
  setModels,
} from '../../features/installer/installerJourneySlice';
import { getCpManufacturers, getCpModelsByManufacturer } from '../../firebase';

const ChargepointSelect = ({
  manufacturer,
  residential,
  model,
  count,
  onChangeManufacturer,
  onChangeModel,
  onChangeCount,
  isSubmitted,
  withChargepointCount,
  hasError,
  setSelectChargepointError,
}) => {
  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;
  const manufacturerModels = useSelector(getManufacturerModels(residential));
  const dispatch = useDispatch();
  const errorArray = [];

  const errorBlankChargepointManufacturer = {
    children: copyProvider.getCopy(
      'installer.ChargepointsDetails.s9',
      'Select a manufacturer'
    ),
  };

  const errorBlankChargepointModel = {
    children: copyProvider.getCopy(
      'installer.ChargepointsDetails.s10',
      'Select a model'
    ),
  };

  const errorBlankNumberChargepoints = {
    children: copyProvider.getCopy(
      'installer.ChargepointsDetails.s11',
      'Enter the number of chargepoints installed'
    ),
  };

  const errorInvalidNumberChargepoints = {
    children: copyProvider.getCopy(
      'installer.ChargepointsDetails.s12',
      'Enter a numeric character greater than 0'
    ),
  };

  const handleChargepointManufacturer = (value) => {
    onChangeManufacturer(value);

    // Don't repopulate chargepoint model redux data if it already exists
    if (
      manufacturerModels &&
      (value.length < 1 ||
        !(value in manufacturerModels) ||
        manufacturerModels[value].length > 0)
    ) {
      return;
    }

    getCpModelsByManufacturer({
      manufacturer_name: value,
      get_socket_count: true,
      residential,
    }).then((response) => {
      const { data } = response;
      const { models } = data;

      dispatch(
        setModels({
          residential,
          manufacturer: value,
          models,
        })
      );
    });
  };

  useEffect(() => {
    if (manufacturerModels === null) {
      getCpManufacturers({ residential }).then((response) => {
        const { data } = response;
        const { manufacturers } = data;

        const manufacturersArray = manufacturers.map((m) => m.value);

        dispatch(
          setManufacturers({
            residential,
            manufacturers: manufacturersArray,
          })
        );
        if (manufacturer) {
          handleChargepointManufacturer(manufacturer);
        }
      });
    }
  }, []);

  const handleChargepointModel = (value) => {
    const numberOfSocketsModel = manufacturerModels[manufacturer].reduce(
      (acc, { name, numberOfSockets }) => {
        if (name === value) {
          return Number(numberOfSockets);
        }

        return acc;
      },
      0
    );

    onChangeModel(value, numberOfSocketsModel);
  };

  const manufacturers = () => {
    if (manufacturerModels === null) {
      return [];
    }
    const items = Object.keys(manufacturerModels).map((m) => {
      return {
        children: m,
        value: m,
      };
    });

    items.unshift({
      children: '',
      value: '',
    });

    return items;
  };

  const models = () => {
    if (manufacturerModels === null || !(manufacturer in manufacturerModels)) {
      return [];
    }

    const modelsOutput = manufacturerModels[manufacturer].map(
      ({ numberOfSockets, name }) => {
        const socketsText =
          numberOfSockets > 1
            ? copyProvider.getCopy(
                'installer.ChargepointsDetails.s13',
                'sockets'
              )
            : copyProvider.getCopy(
                'installer.ChargepointsDetails.s14',
                'socket'
              );
        const nameWithSocketCount = `${name} (${numberOfSockets} ${socketsText})`;
        return { value: name, children: nameWithSocketCount };
      }
    );

    modelsOutput.unshift({
      children: '',
      value: '',
    });

    return modelsOutput;
  };

  const manufacturerError = () => {
    if (isSubmitted && manufacturer === '') {
      errorArray.push({
        text: errorBlankChargepointManufacturer,
        href: '#chargepoint-manufacturer',
      });
      return errorBlankChargepointManufacturer;
    }
    errorArray.filter(
      (error) => error.text !== errorBlankChargepointManufacturer
    );
    return null;
  };

  const modelError = () => {
    if (isSubmitted && model === '') {
      errorArray.push({
        text: errorBlankChargepointModel,
        href: '#chargepoint-model',
      });
      return errorBlankChargepointModel;
    }
    errorArray.filter((error) => error.text !== errorBlankChargepointModel);
    return null;
  };

  const chargepointCountError = () => {
    if (!isSubmitted || !withChargepointCount) {
      return null;
    }
    if (count === '') {
      errorArray.push({
        text: errorBlankNumberChargepoints,
        href: '#number-chargepoints',
      });
      return errorBlankNumberChargepoints;
    }
    errorArray.filter((error) => error.text !== errorBlankNumberChargepoints);
    if (Number(count) <= 0) {
      errorArray.push({
        text: errorInvalidNumberChargepoints,
        href: '#number-chargepoints',
      });
      return errorInvalidNumberChargepoints;
    }
    errorArray.filter((error) => error.text !== errorInvalidNumberChargepoints);
    return null;
  };

  useEffect(() => {
    const error =
      manufacturerError() !== null ||
      modelError() !== null ||
      chargepointCountError() !== null;
    hasError(error);
    setSelectChargepointError(errorArray);
  }, [isSubmitted, manufacturer, model, count]);

  return (
    <>
      <Select
        errorMessage={manufacturerError()}
        onChange={(event) => handleChargepointManufacturer(event.target.value)}
        className="app-select-width-40"
        id="chargepoint-manufacturer"
        name="chargepoint-manufacturer"
        value={manufacturer}
        label={{
          children: copyProvider.getCopy(
            'installer.ChargepointsDetails.s3',
            'Chargepoint manufacturer'
          ),
        }}
        items={manufacturers()}
      />
      <Select
        errorMessage={modelError()}
        onChange={(event) => handleChargepointModel(event.target.value)}
        className="app-select-width-20"
        id="chargepoint-model"
        name="chargepoint-model"
        value={model}
        label={{
          children: copyProvider.getCopy(
            'installer.ChargepointsDetails.s4',
            'Chargepoint model'
          ),
        }}
        items={models()}
      />
      {withChargepointCount && (
        <Input
          errorMessage={chargepointCountError()}
          onChange={(event) => onChangeCount(event.target.value)}
          value={count}
          className="govuk-input--width-4"
          id="number-chargepoints"
          label={{
            children: copyProvider.getCopy(
              'installer.ChargepointsDetails.s5',
              'Number of chargepoints installed for this model'
            ),
          }}
          name="number-chargepoints"
          type="text"
        />
      )}
    </>
  );
};

ChargepointSelect.propTypes = {
  residential: PropTypes.bool,
  manufacturer: PropTypes.string,
  model: PropTypes.string,
  count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChangeManufacturer: PropTypes.func,
  onChangeModel: PropTypes.func,
  onChangeCount: PropTypes.func,
  isSubmitted: PropTypes.bool,
  withChargepointCount: PropTypes.bool,
  hasError: PropTypes.func,
  setSelectChargepointError: PropTypes.func,
};

ChargepointSelect.defaultProps = {
  residential: true,
  manufacturer: '',
  model: '',
  count: '',
  onChangeManufacturer: () => null,
  onChangeModel: () => null,
  onChangeCount: () => null,
  isSubmitted: false,
  withChargepointCount: true,
  hasError: () => null,
  setSelectChargepointError: () => null,
};

export default ChargepointSelect;
